// Utility for managing redirect intent through login/signup flows

const REDIRECT_INTENT_KEY = 'redirect_intent';

export interface RedirectIntent {
  path: string;
  timestamp: number;
  source?: string; // 'demo-results', 'upload', etc.
}

/**
 * Store redirect intent for after login
 */
export function setRedirectIntent(path: string, source?: string): void {
  console.log('[redirectIntent.setRedirectIntent] Setting redirect intent:', { path, source });

  const intent: RedirectIntent = {
    path,
    timestamp: Date.now(),
    source
  };

  localStorage.setItem(REDIRECT_INTENT_KEY, JSON.stringify(intent));
}

/**
 * Get and clear redirect intent
 */
export function getAndClearRedirectIntent(): RedirectIntent | null {
  console.log('[redirectIntent.getAndClearRedirectIntent] Checking for redirect intent...');

  try {
    const stored = localStorage.getItem(REDIRECT_INTENT_KEY);
    if (!stored) {
      console.log('[redirectIntent.getAndClearRedirectIntent] No redirect intent found');
      return null;
    }

    const intent: RedirectIntent = JSON.parse(stored);
    
    // Check if intent is not too old (expire after 30 minutes)
    const ageMinutes = (Date.now() - intent.timestamp) / (1000 * 60);
    if (ageMinutes > 30) {
      console.log('[redirectIntent.getAndClearRedirectIntent] Redirect intent expired:', ageMinutes.toFixed(1), 'minutes old');
      clearRedirectIntent();
      return null;
    }

    console.log('[redirectIntent.getAndClearRedirectIntent] Found valid redirect intent:', intent);
    
    // Clear it since we're consuming it
    clearRedirectIntent();
    
    return intent;
  } catch (error) {
    console.error('[redirectIntent.getAndClearRedirectIntent] Error parsing redirect intent:', error);
    clearRedirectIntent();
    return null;
  }
}

/**
 * Clear redirect intent
 */
export function clearRedirectIntent(): void {
  console.log('[redirectIntent.clearRedirectIntent] Clearing redirect intent');
  localStorage.removeItem(REDIRECT_INTENT_KEY);
}

/**
 * Check if there's a pending redirect intent
 */
export function hasRedirectIntent(): boolean {
  const stored = localStorage.getItem(REDIRECT_INTENT_KEY);
  return !!stored;
}

/**
 * Generate login URL with redirect intent
 */
export function getLoginUrlWithRedirect(currentPath: string, source?: string): string {
  // Set the redirect intent for after login
  setRedirectIntent(currentPath, source);
  return '/login';
}

/**
 * Generate signup URL with redirect intent  
 */
export function getSignupUrlWithRedirect(currentPath: string, source?: string): string {
  // Set the redirect intent for after signup
  setRedirectIntent(currentPath, source);
  return '/signup';
} 